import { Badge, BodyText, DetailText } from '@utilitywarehouse/hearth-react';

import { BundleTileCopyVariant } from '@constants/featureFlags';
import {
  BasketMobile,
  ChargeAmount,
  ProductType,
} from '@generated/gql/graphql';
import { formatCurrency } from '@utils/format';
import {
  discountedMobileService,
  hasMultipleSimsSelected,
} from '@utils/mobile';

import { BundleTile } from './BundleTile';

interface MobileTileProps {
  mobile: Nullable<BasketMobile>;
  bundleCopyVariant?: BundleTileCopyVariant;
}

interface DiscountProps {
  discountedPrice: ChargeAmount;
  durationMonths: number;
  fullPrice: ChargeAmount;
}

const Discount = ({
  discountedPrice,
  durationMonths,
  fullPrice,
}: DiscountProps) => (
  <>
    <DetailText size="2xl">{formatCurrency(discountedPrice)}</DetailText>
    {durationMonths > 0 && (
      <BodyText as="span" textAlign="right">
        for {durationMonths} months, <br />
        then <DetailText>{formatCurrency(fullPrice)}</DetailText> a month
      </BodyText>
    )}
  </>
);

const MobileTile = ({ mobile, bundleCopyVariant }: MobileTileProps) => {
  if (!mobile) return null;

  const { selectedSimCount } = mobile;
  const isSelected = selectedSimCount > 0;
  const {
    total,
    afterPromoTotal,
    durationMonths,
    hasUnlimitedMaxPromo,
    hasMultiSimPromo,
  } = discountedMobileService(mobile);
  const hasDiscount = hasUnlimitedMaxPromo || hasMultiSimPromo;

  const tileProps = {
    productType: ProductType.Mobile,
    price: hasDiscount ? afterPromoTotal : total,
    isSelected,
    bundleCopyVariant,
    compact: true,
    discount: hasDiscount
      ? {
          price: total,
          durationMonths: durationMonths ?? 0,
        }
      : null,
    ...(hasUnlimitedMaxPromo && {
      renderDiscount: () => (
        <Discount
          discountedPrice={total}
          durationMonths={durationMonths ?? 0}
          fullPrice={afterPromoTotal}
        />
      ),
    }),
  };

  if (hasMultipleSimsSelected(mobile)) {
    return (
      <label className="bundle-tile__container">
        <Badge
          className="bundle-tile__two-services-badge"
          size="sm"
          variant="subtle"
          colorScheme="mobile"
        >
          Counts as two services
        </Badge>
        <BundleTile {...tileProps} />
      </label>
    );
  }

  return <BundleTile {...tileProps} />;
};

export { MobileTile };
